import { useEffect } from 'react';

import { Spinner } from '@uikit';

import Checked3 from '@uikit/icons/Checked3';

import { DialogProps } from '@radix-ui/react-dialog';
import { CustomDialog } from '@ui/uikit/components/common/Dialog';
import { Rejected } from '@ui/uikit/components/icons/mono/Rejected';

import { SwapOperationStatus, SwapOperationType } from '../types';
import { SwapOperation } from '../utilities/SwapTransactionExecutor';

interface SwapOperationStatusModalProps extends DialogProps {
  operation?: SwapOperation;
  onClose?(): void;
}

interface OperationInProgressScreenProps {
  operation: SwapOperation;
}

const OperationInProgressScreen = ({ operation }: OperationInProgressScreenProps) => {
  return (
    <div className="flex flex-col py-[16px] gap-[16px] items-center justify-center">
      <div className="text-center text-sm">
        You transaction has been recorded and will be processed shortly.
      </div>

      <div>
        <Spinner size="md" />
      </div>

      {operation.type === SwapOperationType.CrossChain && (
        <div className="text-center text-sm">
          Please keep this page open until the transaction is completed. Do not close this page or
          switch to a different tab.
        </div>
      )}
    </div>
  );
};

const TransactionCompletedScreen = () => {
  return (
    <div className="flex flex-col py-[16px] gap-[16px] items-center justify-center">
      <div className="text-center text-sm">You transaction has completed successfully.</div>

      <div>
        <Checked3 className="icon-[48px] text-green-800" />
      </div>
    </div>
  );
};

interface TransactionFailedScreenProps {
  error: string;
}

const TransactionFailedScreen = ({ error }: TransactionFailedScreenProps) => {
  return (
    <div className="flex flex-col py-[16px] gap-[16px] items-center justify-center">
      <div className="text-center text-sm">You transaction has failed.</div>

      <div>
        <Rejected className="icon-[48px]" />
      </div>

      <div className="w-full p-[16px] rounded-md bg-secondary-100 text-sm">{error}</div>
    </div>
  );
};

const SELF_CLOSE_TIMEOUTS_PER_OPERATION_STATUS_MS: Partial<Record<SwapOperationStatus, number>> =
  {};

const OPERATION_STATUS_MODAL_TITLES_BY_STATUS: Record<SwapOperationStatus, string> = {
  [SwapOperationStatus.Unknown]: 'Transaction status unknown...',
  [SwapOperationStatus.Pending]: 'Transaction in progress...',
  [SwapOperationStatus.Completed]: 'Transaction completed',
  [SwapOperationStatus.Failed]: 'Transaction failed',
};

const SwapOperationStatusModal = ({
  operation,
  onClose,
  ...props
}: SwapOperationStatusModalProps) => {
  console.log('SwapOperationStatusModal::operation', operation);

  useEffect(() => {
    if (operation) {
      const timeout = SELF_CLOSE_TIMEOUTS_PER_OPERATION_STATUS_MS[operation.status];

      if (timeout && onClose) {
        const timer = window.setTimeout(() => {
          console.log('Closing modal');
          onClose();
        }, timeout);

        return () => {
          window.clearTimeout(timer);
        };
      }
    }
  }, [operation, onClose]);

  return operation ? (
    <CustomDialog
      {...props}
      title={OPERATION_STATUS_MODAL_TITLES_BY_STATUS[operation.status]}
      titleIsLeftAligned
      size="sm"
      className="p-[24px]"
      onOpenChange={onClose}
    >
      {operation.status === SwapOperationStatus.Pending && (
        <OperationInProgressScreen operation={operation} />
      )}

      {operation.status === SwapOperationStatus.Completed && <TransactionCompletedScreen />}

      {operation.status === SwapOperationStatus.Failed && (
        <TransactionFailedScreen error={operation.error} />
      )}
    </CustomDialog>
  ) : null;
};

export { SwapOperationStatusModal };
